import {
  Annotation,
  AnnotationWithoutComputedProperties,
  BoundingBox,
  ImageAnnotationWithoutComputedProperties,
  PathAnnotationWithoutComputedProperties,
  Point2D,
  ShapeAnnotationWithoutComputedProperties,
  ShapeType,
  StrokeStyle,
  TextAnnotationWithoutComputedProperties,
} from '@paragon/contract-library';

import {
  annotationToPaperItem,
  getBoundingBoxFromItem,
  getPositionInPdfCoordinates,
  PageImageInfo,
} from './paperItemTranslator';

export function annotationBaseDefaultsFactory() {
  return {
    guid: crypto.randomUUID(),
    sharedWith: 'owner' as const,
    link: null,
    selected: false,
    locked: false,
  };
}

export function duplicateAnnotationWithNewGuid(annotation: Annotation) {
  return {
    ...annotation,
    guid: crypto.randomUUID(),
  };
}

export function textAnnotationDefaultsFactory() {
  return {
    ...annotationBaseDefaultsFactory(),
    type: 'text' as const,
  };
}

export function imageAnnotationDefaultsFactory() {
  return {
    ...annotationBaseDefaultsFactory(),
    type: 'image' as const,
    color: '#FFF',
    rotation: 0,
  };
}

export function shapeAnnotationDefaultsFactory() {
  return {
    ...annotationBaseDefaultsFactory(),
    type: 'shape' as const,
    contents: null,
  };
}

export function pathAnnotationDefaultsFactory() {
  return {
    ...annotationBaseDefaultsFactory(),
    type: 'path' as const,
    contents: null,
    strokeWidth: 1,
    strokeStyle: 'solid' as const,
  };
}

export interface StyleOptions {
  readonly isItalic?: boolean;
  readonly isBold?: boolean;
  readonly isUnderlined?: boolean;
}

export function mockTextAnnotationBoundingBox(
  contents: string,
  fontSize: number,
  fontFamily: string | null,
  position: Point2D,
  rotationAngle: number,
  styleOptions: StyleOptions,
  pageImageInfo: PageImageInfo,
) {
  const annotation: TextAnnotationWithoutComputedProperties = {
    ...textAnnotationDefaultsFactory(),
    contents,
    color: '#000',
    fontSize,
    leading: fontSize,
    fontFamily,
    position,
    boundingBox: null,
    zIndex: 0,
    creatorEmail: 'someone@somewhere.con',
    rotation: rotationAngle,
    selected: true,
    ...styleOptions,
  };

  return mockAnnotationBoundingBox(annotation, pageImageInfo);
}

export function mockImageAnnotationBoundingBox(
  source: string,
  position: Point2D,
  scaling: Point2D,
  rotationAngle: number,
  pageImageInfo: PageImageInfo,
) {
  const annotation: ImageAnnotationWithoutComputedProperties = {
    ...imageAnnotationDefaultsFactory(),
    source,
    position,
    boundingBox: null,
    zIndex: 0,
    creatorEmail: 'someone@somewhere.com',
    scale: scaling,
    rotation: rotationAngle,
    selected: true,
  };

  return mockAnnotationBoundingBox(annotation, pageImageInfo);
}

export function mockShapeAnnotationBoundingBox(
  shapeType: ShapeType,
  shapeGeometry: { readonly width: number; readonly height: number },
  position: Point2D,
  rotationAngle: number,
  pageImageInfo: PageImageInfo,
) {
  const annotation: ShapeAnnotationWithoutComputedProperties = {
    ...shapeAnnotationDefaultsFactory(),
    shapeType,
    width: shapeGeometry.width,
    height: shapeGeometry.height,
    color: '#000',
    position,
    boundingBox: null,
    zIndex: 0,
    creatorEmail: 'someone@somewhere.com',
    rotation: rotationAngle,
    selected: true,
  };

  return mockAnnotationBoundingBox(annotation, pageImageInfo);
}

export function mockPathAnnotationBoundingBoxAndPosition(
  segments: readonly Point2D[],
  strokeWidth: number,
  strokeStyle: StrokeStyle,
  pageImageInfo: PageImageInfo,
) {
  const annotation: PathAnnotationWithoutComputedProperties = {
    ...pathAnnotationDefaultsFactory(),
    segments,
    color: '#000',
    position: null,
    boundingBox: null,
    zIndex: 0,
    creatorEmail: 'someone@somewhere.com',
    rotation: 0,
    strokeWidth,
    strokeStyle,
  };

  const paperItem = annotationToPaperItem(annotation, pageImageInfo);

  const boundingBox = getBoundingBoxFromItem(paperItem, pageImageInfo);
  const position = getPositionInPdfCoordinates(paperItem.position, pageImageInfo);

  paperItem.remove();

  return { boundingBox, position };
}

export function mockAnnotationBoundingBox(
  annotation: ImageAnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
): Promise<BoundingBox>;
export function mockAnnotationBoundingBox(
  annotation: AnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
): BoundingBox;
export function mockAnnotationBoundingBox(
  annotation: AnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
) {
  const paperItem = annotationToPaperItem(annotation, pageImageInfo);

  const getBoundingBox = () => {
    const result = getBoundingBoxFromItem(paperItem, pageImageInfo);

    paperItem.remove();
    return result;
  };

  if (annotation.type === 'image') {
    return new Promise((resolve) => {
      (paperItem as paper.Raster).onLoad = () => {
        resolve(getBoundingBox());
      };
    });
  }

  return getBoundingBox();
}
